import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MapPin, Phone, Clock, CheckCircle } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://www.echt.gold';

const CityPage = ({ citySlug: propCitySlug }) => {
  // If no citySlug prop, try to extract from URL
  const city = propCitySlug || window.location.pathname.split('/').pop().replace('ankauf-', '');
  const citySlug = city; // Define citySlug for use in the component
  
  // Convert URL param to display name
  const cityMap = {
    'aachen': 'Aachen',
    'bielefeld': 'Bielefeld',
    'bochum': 'Bochum',
    'bonn': 'Bonn',
    'bottrop': 'Bottrop',
    'dortmund': 'Dortmund',
    'duisburg': 'Duisburg',
    'duesseldorf': 'Düsseldorf',
    'essen': 'Essen',
    'gelsenkirchen': 'Gelsenkirchen',
    'hagen': 'Hagen',
    'hamm': 'Hamm',
    'herne': 'Herne',
    'koeln': 'Köln',
    'krefeld': 'Krefeld',
    'leverkusen': 'Leverkusen',
    'moenchengladbach': 'Mönchengladbach',
    'muelheim-an-der-ruhr': 'Mülheim an der Ruhr',
    'muenster': 'Münster',
    'oberhausen': 'Oberhausen',
    'remscheid': 'Remscheid',
    'solingen': 'Solingen',
    'wuppertal': 'Wuppertal'
  };

  const cityName = cityMap[city] || city;
  // City-specific information for better local SEO
  const cityInfo = {
    'dortmund': {
      landmarks: 'Westfalenpark, Signal Iduna Park, Dortmunder U',
      districts: 'Innenstadt, Hörde, Hombruch, Aplerbeck, Brackel',
      transport: 'Hauptbahnhof Dortmund, U-Bahn-Linien U41-U49, zahlreiche Busverbindungen',
      description: 'Als größte Stadt im Ruhrgebiet ist Dortmund ein wichtiger Wirtschaftsstandort. Wir bieten professionellen Gold- und Silberankauf in allen Stadtteilen.'
    },
    'koeln': {
      landmarks: 'Kölner Dom, Rheinufer, Hohenzollernbrücke',
      districts: 'Innenstadt, Ehrenfeld, Lindenthal, Nippes, Kalk',
      transport: 'Hauptbahnhof Köln, S-Bahn, U-Bahn und Stadtbahn-Linien',
      description: 'In der Rheinmetropole Köln sind wir Ihr kompetenter Partner für den Gold- und Silberankauf sowie Luxusuhren.'
    },
    'essen': {
      landmarks: 'Zeche Zollverein, Grugapark, Alte Synagoge',
      districts: 'Stadtmitte, Rüttenscheid, Werden, Steele, Kettwig',
      transport: 'Hauptbahnhof Essen, U-Bahn U11-U18, S-Bahn-Linien',
      description: 'Im Herzen des Ruhrgebiets gelegen, bieten wir in Essen professionellen Service beim Gold- und Silberankauf.'
    },
    'duesseldorf': {
      landmarks: 'Königsallee, Rheinturm, Altstadt, MedienHafen',
      districts: 'Stadtmitte, Pempelfort, Oberkassel, Bilk, Benrath',
      transport: 'Hauptbahnhof Düsseldorf, Flughafen, Rheinbahn U-Bahn und Straßenbahn',
      description: 'In der Landeshauptstadt NRW sind wir für Sie da - diskreter und fairer Gold- und Silberankauf sowie Luxusuhren.'
    },
    'bochum': {
      landmarks: 'Deutsches Bergbau-Museum, Jahrhunderthalle, Schauspielhaus',
      districts: 'Innenstadt, Wattenscheid, Langendreer, Querenburg',
      transport: 'Hauptbahnhof Bochum, U-Bahn U35, zahlreiche Buslinien',
      description: 'Zentral im Ruhrgebiet gelegen, bieten wir in Bochum erstklassigen Service beim Gold- und Silberankauf.'
    },
    'duisburg': {
      landmarks: 'Innenhafen, Landschaftspark Nord, Zoo Duisburg',
      districts: 'Mitte, Hamborn, Meiderich, Walsum, Homberg',
      transport: 'Hauptbahnhof Duisburg, S-Bahn, Straßenbahn-Linien',
      description: 'Als Hafenstadt an Rhein und Ruhr sind wir in Duisburg Ihr vertrauensvoller Partner für Gold- und Silberankauf.'
    },
    'wuppertal': {
      landmarks: 'Schwebebahn, Von der Heydt-Museum, Hardt-Anlagen',
      districts: 'Elberfeld, Barmen, Vohwinkel, Cronenberg',
      transport: 'Hauptbahnhof Wuppertal, Schwebebahn, S-Bahn-Linien',
      description: 'In der Stadt der Schwebebahn bieten wir professionellen Ankauf von Gold, Silber und Luxusuhren.'
    },
    'muenster': {
      landmarks: 'Prinzipalmarkt, St.-Paulus-Dom, Aasee',
      districts: 'Innenstadt, Gievenbeck, Hiltrup, Kinderhaus, Mauritz',
      transport: 'Hauptbahnhof Münster, Stadtbus-Linien, Fahrradstadt',
      description: 'In der Fahrrad- und Universitätsstadt Münster sind wir Ihr kompetenter Partner für Gold- und Silberankauf.'
    },
    'moenchengladbach': {
      landmarks: 'Schloss Rheydt, Bunter Garten, Abteiberg Museum',
      districts: 'Stadtmitte, Rheydt, Hardt, Eicken, Windberg',
      transport: 'Hauptbahnhof Mönchengladbach, S-Bahn, Buslinien',
      description: 'Am Niederrhein gelegen, bieten wir in Mönchengladbach fairen Gold- und Silberankauf sowie Uhren.'
    },
    'aachen': {
      landmarks: 'Aachener Dom, Rathaus, Carolus Thermen',
      districts: 'Innenstadt, Brand, Laurensberg, Richterich',
      transport: 'Hauptbahnhof Aachen, ASEAG Buslinien',
      description: 'In der Kaiserstadt Aachen an der Grenze zu Belgien und den Niederlanden bieten wir erstklassigen Gold- und Silberankauf.'
    },
    'bielefeld': {
      landmarks: 'Sparrenburg, Kunsthalle, Alter Markt',
      districts: 'Mitte, Schildesche, Sennestadt, Brackwede',
      transport: 'Hauptbahnhof Bielefeld, Stadtbahn-Linien',
      description: 'In Ostwestfalen-Lippe sind wir in Bielefeld Ihr vertrauensvoller Partner für Gold- und Silberankauf.'
    },
    'bonn': {
      landmarks: 'Beethovenhaus, Münster, Poppelsdorfer Schloss',
      districts: 'Stadtbezirk Bonn, Bad Godesberg, Beuel, Hardtberg',
      transport: 'Hauptbahnhof Bonn, U-Bahn, S-Bahn-Linien',
      description: 'In der ehemaligen Bundeshauptstadt Bonn bieten wir professionellen Ankauf von Gold, Silber und Luxusuhren.'
    }
  };

  const defaultInfo = {
    landmarks: 'Stadtmitte und Umgebung',
    districts: 'Alle Stadtteile',
    transport: 'Gute Verkehrsanbindung',
    description: `Wir sind für Sie in ${cityName} und Umgebung für den Gold- und Silberankauf, Schmuck und Luxusuhren da.`
  };

  const localInfo = cityInfo[city] || defaultInfo;

  const faqs = [
    {
      q: `Wie hoch sind die Ankaufspreise in ${cityName}?`,
      a: `Unsere Ankaufspreise in ${cityName} orientieren sich am aktuellen Tageskurs der Edelmetalle. Gold, Silber, Platin und Palladium werden täglich an der Börse gehandelt - wir bieten Ihnen faire Preise basierend auf diesen Kursen. Die Preise sind einheitlich in ganz NRW.`
    },
    {
      q: `Wie läuft die Prüfung und Analyse in ${cityName} ab?`,
      a: `Bei Ihrem Termin in ${cityName} prüfen unsere Experten Ihre Wertgegenstände mit modernen Analysegeräten. Wir ermitteln den Feingehalt und das Gewicht präzise. Die Bewertung erfolgt transparent vor Ihren Augen. Bei Vor-Ort-Terminen bringen wir unsere professionelle Ausrüstung mit.`
    },
    {
      q: `Kann ich in ${cityName} bar ausgezahlt werden?`,
      a: `Ja, nach erfolgreicher Prüfung und Ihrer Zustimmung zum Angebot erfolgt die Auszahlung sofort - wahlweise bar (bis 10.000€) oder per Überweisung. Gemäß Geldwäschegesetz benötigen wir einen gültigen Personalausweis.`
    },
    {
      q: `Muss ich in ${cityName} einen Termin vereinbaren?`,
      a: `Für einen reibungslosen Ablauf in ${cityName} empfehlen wir eine Terminvereinbarung. So können wir sicherstellen, dass ein Experte für Sie Zeit hat. Rufen Sie uns an unter 0231 39085116 oder nutzen Sie unser Kontaktformular.`
    },
    {
      q: `In welchen Stadtteilen von ${cityName} sind Sie tätig?`,
      a: `Wir bieten unseren Service in allen Stadtteilen von ${cityName} an: ${localInfo.districts}. Nach Terminvereinbarung kommen wir auch gerne zu Ihnen nach Hause, insbesondere bei größeren Mengen oder hochwertigen Stücken.`
    },
    {
      q: `Kaufen Sie auch Luxusuhren ohne Papiere in ${cityName} an?`,
      a: `Ja, wir kaufen Luxusuhren auch ohne Box und Papiere an. Vollständige Sets mit Original-Dokumentation erhöhen natürlich den Wert, sind aber keine Voraussetzung für den Ankauf in ${cityName}.`
    },
    {
      q: `Wie werden Diamanten in ${cityName} bewertet?`,
      a: `Unsere Diamantenexperten bewerten nach den international anerkannten 4C: Carat (Gewicht), Cut (Schliff), Color (Farbe) und Clarity (Reinheit). Zertifikate von GIA, HRD oder IGI sind hilfreich, aber nicht zwingend erforderlich. Wir führen professionelle Bewertungen direkt bei Ihnen in ${cityName} durch.`
    },
    {
      q: `Wie komme ich am besten zu Ihrem Standort von ${cityName} aus?`,
      a: `Von ${cityName} aus erreichen Sie unseren Hauptstandort in Dortmund bequem: ${localInfo.transport}. Unser Standort ist gut erreichbar. Alternativ bieten wir auch mobile Termine direkt bei Ihnen in ${cityName} an.`
    }
  ];

  return (
    <>
      <Helmet>
        <title>Uhren-, Schmuck- & Gold- und Silberankauf in {cityName} | Echt Gold</title>
        <meta name="description" content={`Seriöser Gold- und Silberankauf, Uhren & Schmuck in ${cityName}. ${localInfo.description} Kostenlose Bewertung, faire Preise. Jetzt Termin buchen!`} />
        <link rel="canonical" href={`${BASE_URL}/ankauf-${citySlug}`} />
        <meta name="keywords" content={`Goldankauf ${cityName}, Silberankauf ${cityName}, Uhrenankauf ${cityName}, Schmuckankauf ${cityName}, Gold und Silber Ankauf ${cityName}`} />
      </Helmet>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": `Gold- und Silberankauf, Uhren & Schmuck in ${cityName}`,
          "description": localInfo.description,
          "provider": {
            "@type": "LocalBusiness",
            "name": "Echt Gold",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Neben dem Brand 10",
              "addressLocality": "Dortmund",
              "postalCode": "44135",
              "addressCountry": "DE"
            },
            "telephone": "+49-231-39085116",
            "email": "Info@echtgold.de"
          },
          "areaServed": {
            "@type": "City",
            "name": cityName,
            "description": `Wir bieten unseren Service in ${cityName} und allen Stadtteilen an: ${localInfo.districts}`
          },
          "serviceType": "Gold- und Silberankauf, Goldankauf, Silberankauf, Uhrenankauf, Schmuckankauf",
          "availableChannel": {
            "@type": "ServiceChannel",
            "servicePhone": {
              "@type": "ContactPoint",
              "telephone": "+49-231-39085116",
              "contactType": "customer service"
            }
          }
        })}
      </script>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.a
            }
          }))
        })}
      </script>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Home", "item": `${BASE_URL}/`},
            {"@type": "ListItem", "position": 2, "name": `Ankauf ${cityName}`, "item": `${BASE_URL}/ankauf-${citySlug}`}
          ]
        })}
      </script>

      <div className="bg-black/90 py-8 border-b border-gold/20">
        <div className="container mx-auto px-4">
          <div className="text-sm text-gray-400 mb-2">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-gold">Ankauf {cityName}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold gold-text">Gold- und Silberankauf, Uhren & Schmuck in {cityName}</h1>
        </div>
      </div>

      <section className="section section-dark">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <div className="trust-badge">
                <CheckCircle className="text-gold" size={20} />
                <span>Kostenlose Bewertung</span>
              </div>
              <div className="trust-badge">
                <CheckCircle className="text-gold" size={20} />
                <span>Faire Tagespreise</span>
              </div>
              <div className="trust-badge">
                <CheckCircle className="text-gold" size={20} />
                <span>Diskret & Schnell</span>
              </div>
            </div>

            <p className="text-gray-300 text-lg mb-8 text-center">
              {localInfo.description}
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="card">
                <h2 className="text-2xl font-bold mb-4 gold-text">So läuft der Ankauf ab</h2>
                <ol className="space-y-3 text-gray-300">
                  <li className="flex gap-3">
                    <span className="text-gold font-bold">1.</span>
                    <span><strong>Termin vereinbaren:</strong> Kontaktieren Sie uns für einen Termin in {cityName} oder nutzen Sie unseren Versandankauf.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-gold font-bold">2.</span>
                    <span><strong>Bewertung:</strong> Unsere Experten prüfen Ihre Wertgegenstände professionell und transparent.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-gold font-bold">3.</span>
                    <span><strong>Angebot:</strong> Sie erhalten ein faires Angebot basierend auf aktuellen Tageskursen.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-gold font-bold">4.</span>
                    <span><strong>Auszahlung:</strong> Bei Annahme erfolgt sofortige Auszahlung (bar oder Überweisung).</span>
                  </li>
                </ol>
                <p className="text-sm text-gray-400 mt-4 bg-gold/10 p-3 rounded border border-gold/30">
                  <strong>Wichtig:</strong> Gemäß Geldwäschegesetz ist eine Legitimationsprüfung (Personalausweis) erforderlich.
                </p>
              </div>

              <div className="card">
                <h2 className="text-2xl font-bold mb-4 gold-text">Was wir in {cityName} ankaufen</h2>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={18} />
                    <span><strong>Gold:</strong> Schmuck, Barren, Münzen, Zahngold</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={18} />
                    <span><strong>Silber:</strong> Barren, Münzen, Schmuck, Tafelsilber</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={18} />
                    <span><strong>Platin & Palladium:</strong> Alle Formen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={18} />
                    <span><strong>Luxusuhren:</strong> Rolex, Omega, Patek Philippe, Breitling, IWC, Cartier uvm.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={18} />
                    <span><strong>Diamanten:</strong> Lose Steine und Diamantschmuck</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-darker">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 gold-text text-center">Servicegebiet & Anfahrt nach {cityName}</h2>
            <p className="text-gray-300 mb-8 text-center">
              Unser Hauptsitz befindet sich in Waltrop. Für Kunden aus {cityName} bieten wir:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="card text-center">
                <MapPin className="text-gold mx-auto mb-3" size={32} />
                <h3 className="font-bold mb-2">Vor-Ort-Termin</h3>
                <p className="text-gray-300 text-sm">Nach Vereinbarung kommen wir zu Ihnen in {cityName}</p>
              </div>
              <div className="card text-center">
                <Phone className="text-gold mx-auto mb-3" size={32} />
                <h3 className="font-bold mb-2">Termin in Waltrop</h3>
                <p className="text-gray-300 text-sm">Besuchen Sie uns in unserem Geschäft</p>
              </div>
              <div className="card text-center">
                <Clock className="text-gold mx-auto mb-3" size={32} />
                <h3 className="font-bold mb-2">Versandankauf</h3>
                <p className="text-gray-300 text-sm">Bequem von zu Hause - mit kostenlosem DHL-Label</p>
              </div>
            </div>

            <div className="mt-8 p-6 bg-gold/10 border border-gold/30 rounded-lg">
              <h3 className="text-xl font-bold mb-3 gold-text">Lokale Informationen für {cityName}</h3>
              <div className="grid md:grid-cols-3 gap-4 text-gray-300 text-sm">
                <div>
                  <strong className="text-gold">Bekannte Orte:</strong><br />
                  {localInfo.landmarks}
                </div>
                <div>
                  <strong className="text-gold">Stadtteile:</strong><br />
                  {localInfo.districts}
                </div>
                <div>
                  <strong className="text-gold">Verkehrsanbindung:</strong><br />
                  {localInfo.transport}
                </div>
              </div>
              <p className="text-gray-300 mt-4">
                Egal ob Sie aus der {localInfo.districts.split(',')[0]} oder einem anderen Stadtteil kommen – wir sind flexibel und kommen gerne zu Ihnen. Alternativ können Sie uns auch in Dortmund besuchen.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-dark">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 gold-text text-center">Häufige Fragen zum Ankauf in {cityName}</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="card">
                  <h3 className="text-lg font-bold mb-2 gold-text">{faq.q}</h3>
                  <p className="text-gray-300">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section section-darker">
        <div className="container">
          <div className="card text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 gold-text">Jetzt Termin in {cityName} vereinbaren</h2>
            <p className="text-gray-300 mb-8">
              Kontaktieren Sie uns für eine kostenlose und unverbindliche Bewertung
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/kontakt" className="btn-primary" data-testid={`cta-city-${citySlug}`}>Jetzt Kontakt aufnehmen</Link>
              <a href="tel:023139085116" className="btn-secondary">0231 39085116</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CityPage;